import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CardDivider, CardText } from '@/components/atoms/Card';
import { Caption } from '@/components/atoms/Eyebrow';
import CircularChart, { type CircularSlice } from '@/components/charts/CircularChart';
import { ChartKeySwatch } from '@/components/charts/legend';
import { graphSettings } from '@/config/settings';
import { formatFractionAsPercentageByLocale } from '@/utils/localeFormatters';

import styles from './WoundDescription.module.scss';

/** Wound size colors — small → yellow, medium → orange, large → red ($laksvel-1/2/3). */
export const WOUND_SIZE_COLORS = {
    small: graphSettings.fishHealthColors.small,
    medium: graphSettings.fishHealthColors.medium,
    large: graphSettings.fishHealthColors.large,
} as const;

const MIN_WOUND = 0.0005;

const WOUND_SIZE_LABELS = [
    'molecules.woundDescription.sizes.small',
    'molecules.woundDescription.sizes.medium',
    'molecules.woundDescription.sizes.large',
] as const;

/** The same sizes as they read mid-sentence in the summary. */
const WOUND_SIZE_SUMMARY_LABELS = [
    'molecules.woundDescription.sizesInSentence.small',
    'molecules.woundDescription.sizesInSentence.medium',
    'molecules.woundDescription.sizesInSentence.large',
] as const;

const WOUND_SIZE_CHART_COLORS = [
    WOUND_SIZE_COLORS.small,
    WOUND_SIZE_COLORS.medium,
    WOUND_SIZE_COLORS.large,
] as const;

interface WoundSizeLegendItemProps {
    color: string;
    label: string;
    value: string;
}

function WoundSizeLegendItem(props: WoundSizeLegendItemProps) {
    const { color, label, value } = props;

    return (
        <div className={styles.sizeLegendItem}>
            <ChartKeySwatch shape="dot" color={color} className={styles.sizeLegendDot} />
            <div className={styles.sizeLegendText}>
                <Caption as="div">{label}</Caption>
                <div className={styles.sizeLegendValue}>{value}</div>
            </div>
        </div>
    );
}

interface WoundDescriptionProps {
    showPieChart?: boolean;
    /**
     * `auto` puts the chart beside the bars on a wide screen; `stacked` always puts it above
     * them, for a grid cell narrower than the screen breakpoint assumes.
     */
    layout?: 'auto' | 'stacked';
    woundsColor: {
        noWounds: number | null | undefined;
        /**
         * Active and healing wounds, which only colour bioscopes measure. Leave both out (Bioscope
         * 1.0) and the description says nothing about how far the wounds have healed.
         */
        activeWounds?: number | null;
        healingWounds?: number | null;
        bigWound: number | null | undefined;
        mediumWound: number | null | undefined;
        smallWound: number | null | undefined;
    };
}

function WoundDescription({
    woundsColor,
    showPieChart = false,
    layout = 'auto',
}: WoundDescriptionProps) {
    const { t, i18n } = useTranslation();
    const activeWounds = woundsColor.activeWounds ?? 0;
    const healingWounds = woundsColor.healingWounds ?? 0;
    const mediumWound = woundsColor.mediumWound ?? 0;
    const smallWound = woundsColor.smallWound ?? 0;
    const bigWound = woundsColor.bigWound ?? 0;

    const hasWoundState =
        woundsColor.activeWounds !== undefined || woundsColor.healingWounds !== undefined;

    const woundDistribution = useMemo(
        () => [smallWound, mediumWound, bigWound],
        [smallWound, mediumWound, bigWound]
    );

    const woundDistributionItems = [
        {
            value: smallWound,
            color: WOUND_SIZE_COLORS.small,
            woundSizeDefinition: 'molecules.woundDescription.sizeDefinitions.small',
        },
        {
            value: mediumWound,
            color: WOUND_SIZE_COLORS.medium,
            woundSizeDefinition: 'molecules.woundDescription.sizeDefinitions.medium',
        },
        {
            value: bigWound,
            color: WOUND_SIZE_COLORS.large,
            woundSizeDefinition: 'molecules.woundDescription.sizeDefinitions.large',
        },
    ];

    const largesPortionIndex = woundDistribution.indexOf(Math.max(...woundDistribution));
    const sumWoundSizes = woundDistribution.reduce((a, b) => a + b, 0);
    const sumWoundState = activeWounds + healingWounds;

    const toPercent = (partialValue: number, totalValue: number) => {
        return Math.abs((100 * partialValue) / totalValue);
    };

    const findWoundState = (
        healingWoundPercentage: number,
        activeWoundPercentage: number
    ): string => {
        let msg = '';
        const ratioHealing = healingWoundPercentage / activeWoundPercentage;
        const ratioActive = activeWoundPercentage / healingWoundPercentage;

        const outOfHealing = Math.round(ratioHealing);
        const outOfActive = Math.round(ratioActive);

        if (healingWoundPercentage > activeWoundPercentage) {
            if (healingWoundPercentage - activeWoundPercentage > 95) {
                msg = `${t('molecules.woundDescription.mostOfTheOfTheseWoundsAreHealing')}`;
            } else {
                msg = `${t('molecules.woundDescription.about')} ${outOfHealing} ${t('molecules.woundDescription.outOf')} ${outOfHealing + 1} ${t(
                    'molecules.woundDescription.ofTheseWoundsAreHealing'
                )}`;
            }
        } else if (activeWoundPercentage > healingWoundPercentage) {
            if (activeWoundPercentage - healingWoundPercentage > 95) {
                msg = `${t('molecules.woundDescription.mostOfTheOfTheseWoundsAreActive')}`;
            } else {
                msg = `${t('molecules.woundDescription.about')} ${outOfActive} ${t('molecules.woundDescription.outOf')} ${outOfActive + 1} ${t(
                    'molecules.woundDescription.ofTheseWoundsAreActive'
                )}`;
            }
        } else {
            msg = t('molecules.woundDescription.theShareOfHealingAnd');
        }
        return msg;
    };

    const decriptiveWoundState = hasWoundState
        ? findWoundState(
              toPercent(healingWounds, sumWoundState),
              toPercent(activeWounds, sumWoundState)
          )
        : null;

    const notEnoughDataText =
        sumWoundSizes < MIN_WOUND ? t('molecules.woundDescription.theWoundPercentageIsToo') : '';
    const summaryText =
        sumWoundSizes > 0
            ? `${t('molecules.woundDescription.theWoundsWhichAreRegistered')} ${t(WOUND_SIZE_SUMMARY_LABELS[largesPortionIndex])} ${t('molecules.woundDescription.wounds')}${decriptiveWoundState ? ` ${t('molecules.woundDescription.and')} ${decriptiveWoundState}` : ''}`
            : t('common.labels.noWounds');

    const description = notEnoughDataText.length === 0 ? summaryText : notEnoughDataText;

    const pieSlices: CircularSlice[] = useMemo(
        () =>
            WOUND_SIZE_LABELS.map((label, index) => ({
                label: t(label),
                value: woundDistribution[index] * 100,
                color: WOUND_SIZE_CHART_COLORS[index],
            })),
        [woundDistribution, t]
    );

    return (
        <>
            <div className={styles.woundDescriptionSummary} data-layout={layout}>
                <CardText>{description}</CardText>
            </div>
            <div className={styles.wounddescription} data-layout={layout}>
                {showPieChart && sumWoundSizes >= MIN_WOUND ? (
                    <div className={styles.woundPieChart}>
                        <CircularChart kind="pie" slices={pieSlices} />
                    </div>
                ) : null}

                <div className={styles.wounddescriptionBars}>
                    {woundDistributionItems.map((woundDistributionItem, index) => {
                        const isLastItem = index === woundDistributionItems.length - 1;
                        const formattedValue = Number.isNaN(woundDistributionItem.value)
                            ? '-'
                            : formatFractionAsPercentageByLocale(
                                  woundDistributionItem.value,
                                  i18n.language,
                                  2
                              );

                        return (
                            <div
                                key={woundDistributionItem.woundSizeDefinition}
                                className={styles.woundSizeRow}>
                                <WoundSizeLegendItem
                                    color={woundDistributionItem.color}
                                    label={t(woundDistributionItem.woundSizeDefinition)}
                                    value={formattedValue}
                                />
                                {!isLastItem ? <CardDivider /> : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

export default WoundDescription;
export { WoundDescription };
